import { Link } from "react-router";
import { LogoIcon, LogoText } from "./Logo";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import type { LucideIcon } from "lucide-react";

interface SidebarNavItemProps {
  href: string;
  label: string;
  icon: LucideIcon;
  isActive?: boolean;
}

export function SidebarNavItem({
  href,
  label,
  icon: Icon,
  isActive,
}: SidebarNavItemProps) {
  return (
    <Link
      to={href}
      className={cn(
        "flex items-center gap-3 rounded-md px-3 py-2 text-base transition-colors duration-150 min-w-0 overflow-hidden",
        isActive
          ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold"
          : "text-sidebar-foreground hover:bg-sidebar-accent/50"
      )}
    >
      <Icon
        className={cn(
          "h-[26px] w-[26px] shrink-0",
          isActive ? "stroke-[2.5]" : "stroke-[1.75]"
        )}
      />
      <span className="whitespace-nowrap">{label}</span>
    </Link>
  );
}

interface SidebarShellProps {
  children: React.ReactNode;
  bottom?: React.ReactNode;
}

export function SidebarShell({ children, bottom }: SidebarShellProps) {
  return (
    <aside
      className={cn(
        "group/sidebar fixed inset-y-0 left-0 z-40 hidden border-r border-sidebar-border bg-sidebar overflow-x-hidden",
        "md:flex md:w-16 md:flex-col md:hover:w-[220px] md:hover:shadow-xl",
        "transition-[width,box-shadow] duration-0 hover:duration-200 ease-in-out"
      )}
    >
      <div className="flex h-14 shrink-0 items-center px-4">
        <Link
          to="/"
          className="flex items-center gap-2 text-sidebar-foreground"
        >
          <LogoIcon />
          <LogoText className="hidden group-hover/sidebar:inline xl:inline whitespace-nowrap" />
        </Link>
      </div>

      <Separator className="bg-sidebar-border" />

      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden px-2 py-4">
        {children}
      </nav>

      {bottom && (
        <>
          <Separator className="bg-sidebar-border" />
          <div className="flex flex-col gap-2 px-2 py-3">
            {bottom}
          </div>
        </>
      )}
    </aside>
  );
}
